import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Building2, CheckCircle, XCircle, Clock, ExternalLink, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_STYLES = {
  pending:  'bg-yellow-900/30 text-yellow-400 border-yellow-800',
  approved: 'bg-green-900/30 text-green-400 border-green-800',
  rejected: 'bg-red-900/30 text-red-500 border-red-800',
};

const NonprofitAdminTab = () => {
  const [registrations, setRegistrations] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchRegs(); }, [filter]);

  const fetchRegs = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/nonprofit/registrations${filter !== 'all' ? `?status=${filter}` : ''}`);
      setRegistrations(res.data?.registrations || []);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  };

  const review = async (id, status, orgName) => {
    const verb = status === 'approved' ? 'Approve' : 'Reject';
    if (!window.confirm(`${verb} "${orgName}"?`)) return;
    try {
      await api.patch(`/nonprofit/registrations/${id}`, null, { params: { status } });
      toast.success(`Application ${status}`);
      fetchRegs();
    } catch { toast.error('Failed to update application'); }
  };

  const pendingCount = registrations.filter(r => r.status === 'pending').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#C5A52A]" /> Non-Profit Program
          </h2>
          <p className="text-gray-500 text-sm font-mono mt-1">Review and approve non-profit organization applications</p>
        </div>
        {pendingCount > 0 && (
          <span className="bg-yellow-600 text-black text-xs font-bold px-3 py-1 rounded-full animate-pulse">
            {pendingCount} PENDING
          </span>
        )}
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2 mb-6">
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-mono uppercase rounded transition-colors ${filter === f ? 'bg-[#C5A52A] text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm font-mono text-center py-12">Loading...</p>
      ) : registrations.length === 0 ? (
        <div className="border-2 border-dashed border-gray-800 rounded p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 font-mono text-sm">No {filter !== 'all' ? filter : ''} applications.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {registrations.map(reg => (
            <div key={reg.id} className="bg-gray-900 border border-gray-800 rounded">
              {/* Header row */}
              <div className="flex items-start justify-between p-5 gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-white font-bold text-base">{reg.organization_name}</h3>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_STYLES[reg.status] || 'bg-gray-800 text-gray-400 border-gray-700'}`}>
                      {reg.status?.toUpperCase()}
                    </span>
                    {reg.is_federal_charity && <span className="text-[10px] font-mono text-blue-400 bg-blue-900/20 border border-blue-800/40 px-1.5 py-0.5 rounded">FEDERAL CHARITY</span>}
                    {reg.is_provincial_nonprofit && <span className="text-[10px] font-mono text-purple-400 bg-purple-900/20 border border-purple-800/40 px-1.5 py-0.5 rounded">PROVINCIAL NPO</span>}
                  </div>
                  <div className="text-gray-500 text-xs font-mono space-x-4">
                    <span>{reg.contact_person}</span>
                    <span>·</span>
                    <span>{reg.email}</span>
                    {reg.cyqr_username && <><span>·</span><span className="text-[#C5A52A]">@{reg.cyqr_username}</span></>}
                    <span>·</span>
                    <span>{new Date(reg.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-2">
                  {reg.status === 'pending' && (
                    <>
                      <button onClick={() => review(reg.id, 'approved', reg.organization_name)}
                        className="flex items-center gap-1.5 bg-green-700 hover:bg-green-600 text-white text-xs font-mono px-3 py-2 rounded transition-colors"
                        data-testid={`approve-nonprofit-${reg.id}`}>
                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button onClick={() => review(reg.id, 'rejected', reg.organization_name)}
                        className="flex items-center gap-1.5 bg-red-700 hover:bg-red-600 text-white text-xs font-mono px-3 py-2 rounded transition-colors"
                        data-testid={`reject-nonprofit-${reg.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => setExpanded(expanded === reg.id ? null : reg.id)}
                    className="text-gray-500 hover:text-white p-2 transition-colors">
                    {expanded === reg.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Expanded details */}
              {expanded === reg.id && (
                <div className="border-t border-gray-800 px-5 py-4 space-y-3 bg-gray-950 rounded-b">
                  {reg.mission_statement && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-mono mb-1">Mission Statement</p>
                      <p className="text-gray-300 text-sm">{reg.mission_statement}</p>
                    </div>
                  )}
                  {reg.tax_id && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-mono mb-1">Tax ID / Charity Number</p>
                      <p className="text-gray-300 text-sm font-mono">{reg.tax_id}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-4 flex-wrap">
                    {reg.website_url && (
                      <a href={reg.website_url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 text-xs text-[#C5A52A] hover:underline">
                        <ExternalLink className="w-3 h-3" /> Website
                      </a>
                    )}
                    {reg.proof_document_url && (
                      <a href={reg.proof_document_url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 text-xs text-blue-400 hover:underline">
                        <FileText className="w-3 h-3" /> Proof Document
                      </a>
                    )}
                  </div>
                  {reg.reviewed_at && (
                    <p className="text-gray-600 text-xs font-mono">
                      Reviewed: {new Date(reg.reviewed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NonprofitAdminTab;
